import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, RefreshCw, AlertTriangle, Database, Zap, Bell, Smartphone, Cpu, HardDrive, Wifi, Activity, Server, Timer, Shield, TrendingUp, ChevronDown, ChevronUp, Info } from 'lucide-react';
import axios from 'axios';

const API_BASE = 'https://api.lifeshare.app/api/v1';

interface ServiceInfo { name: string; status: string; latency_ms?: number; detail?: string; icon: string; }
interface SchedulerJob { id: string; name: string; next_run: string | null; status: string; }
interface DailyStatus { date: string; checks: number; uptime_pct: number; ok: number; warning: number; error: number; status: string; services: Record<string, number>; }
interface HistoryData { overall_uptime_pct: number; daily: DailyStatus[]; incidents: DailyStatus[]; last_check: any; services: string[]; }
interface ApiStatusData {
    overall: string; timestamp: string;
    uptime: { seconds: number; formatted: string; start_time: string };
    services: ServiceInfo[];
    scheduler: { running: boolean; jobs: SchedulerJob[]; total: number };
    system: { cpu_percent: number; cpu_count: number; memory_percent: number; memory_total_gb: number; memory_used_gb: number; disk_percent: number; disk_total_gb: number; disk_used_gb: number; net_sent_gb: number; net_recv_gb: number; python_version: string; os: string; };
    database: { size_mb: number; total_users: number; active_users: number; active_devices: number; ios_devices: number; android_devices: number; locations_last_5min: number; };
}
interface EndpointStatus { name: string; endpoint: string; status: 'checking' | 'ok' | 'error' | 'slow' | 'cors'; responseTime?: number; error?: string; category: string; }

const SVC_LABELS: Record<string, string> = { db: 'PostgreSQL Veritabanı', redis: 'Redis Önbellek', apns: 'APNS (iOS Bildirim)', fcm: 'FCM (Android Bildirim)', scheduler: 'Zamanlayıcı' };
const SVC_DESC: Record<string, Record<string, string>> = {
    db: { ok: 'Veritabanı bağlantısı aktif, sorgular normal yanıt veriyor.', error: 'Veritabanına bağlanılamıyor! Konum güncellemeleri ve kullanıcı işlemleri çalışmaz. Acil müdahale gerekli.', warning: 'Veritabanı yanıt süresi normalden yüksek.' },
    redis: { ok: 'Önbellek servisi aktif, doğrulama kodları ve oturum yönetimi çalışıyor.', error: 'Redis bağlantısı kopuk! SMS doğrulama kodları gönderilemez, yeni kayıtlar ve şifre sıfırlama çalışmaz.', warning: 'Redis yanıt süresi yüksek.' },
    apns: { ok: 'Apple Push Notification servisi aktif, iOS bildirimleri gönderiliyor.', error: 'APNS bağlantısı yok! iOS kullanıcılarına bildirim gönderilemez.', warning: 'APNS sertifikası yapılandırılmamış veya süresi dolmak üzere. iOS bildirimleri çalışmayabilir.' },
    fcm: { ok: 'Firebase Cloud Messaging aktif, Android bildirimleri gönderiliyor.', error: 'FCM bağlantısı yok! Android kullanıcılarına bildirim gönderilemez.', warning: 'FCM kimlik bilgileri yapılandırılmamış. Android bildirimleri devre dışı.' },
    scheduler: { ok: 'Zamanlayıcı çalışıyor: silent push, trip kontrolü, bildirimler zamanında gönderiliyor.', error: 'Zamanlayıcı durmuş! Otomatik bildirimler, trip sonlandırma ve konum yenileme çalışmaz.', warning: 'Zamanlayıcıda bazı görevler gecikmiş olabilir.' }
};
const EP_ERR: Record<string, { title: string; desc: string }> = {
    '404': { title: 'Endpoint Bulunamadı', desc: 'Bu endpoint sunucuda mevcut değil. Backend kodunda eksik route veya deploy sırasında dosya atlanmış olabilir.' },
    '500': { title: 'Sunucu İç Hatası', desc: 'Backend loglarını kontrol edin — veritabanı sorgusu veya servis çağrısında beklenmeyen bir hata oluştu.' },
    '502': { title: 'Ağ Geçidi Hatası', desc: 'API sunucusu yanıt vermiyor. Docker container çökmüş veya yeniden başlatılıyor olabilir.' },
    'CORS': { title: 'Tarayıcı Güvenlik Kısıtlaması', desc: 'Bu hata sadece web panelden erişimde görülür. iOS ve Android uygulamalarında sorun yaşanmaz.' },
    'Auth': { title: 'Kimlik Doğrulama Gerekli', desc: 'Endpoint aktif ve çalışıyor, erişim için oturum açmış olmak gerekiyor.' },
    'POST': { title: 'Sadece POST Metodu', desc: 'Bu endpoint çalışıyor ancak yalnızca POST isteklerini kabul ediyor.' }
};

const getSvcKey = (name: string): string => {
    if (name.includes('PostgreSQL')) return 'db';
    if (name.includes('Redis')) return 'redis';
    if (name.includes('APNS')) return 'apns';
    if (name.includes('FCM')) return 'fcm';
    return 'scheduler';
};

const ApiStatus: React.FC = () => {
    const [s, setS] = useState<ApiStatusData | null>(null);
    const [history, setHistory] = useState<HistoryData | null>(null);
    const [loading, setLoading] = useState(true);
    const [eps, setEps] = useState<EndpointStatus[]>([]);
    const [checking, setChecking] = useState(false);
    const [lastCheck, setLastCheck] = useState<Date | null>(null);
    const [epSum, setEpSum] = useState({ ok: 0, error: 0, slow: 0, cors: 0, total: 0 });
    const [selCat, setSelCat] = useState('all');
    const [hovDay, setHovDay] = useState<DailyStatus | null>(null);
    const [autoRef, setAutoRef] = useState(true);
    const [expSvc, setExpSvc] = useState<string | null>(null);
    const [expEp, setExpEp] = useState<number | null>(null);
    const [expInc, setExpInc] = useState<number | null>(null);

    const epList = [
        { n: 'Dashboard', p: '/admin/dashboard', c: 'Sistem' },
        { n: 'Analitik', p: '/admin/analytics', c: 'Sistem' },
        { n: 'Abonelikler', p: '/admin/subscriptions?limit=1', c: 'Sistem' },
        { n: 'Yedekleme', p: '/admin/backup/list', c: 'Sistem' },
        { n: 'API Durumu', p: '/admin/api-status', c: 'Sistem' },
        { n: 'Kullanıcılar', p: '/admin/users?limit=1', c: 'Kullanıcı' },
        { n: 'Cihazlar', p: '/admin/devices', c: 'Kullanıcı' },
        { n: 'Oturumlar', p: '/admin/sessions?limit=1', c: 'Kullanıcı' },
        { n: 'Çevreler', p: '/admin/families', c: 'Çevre' },
        { n: 'Bildirimler', p: '/admin/notifications', c: 'Bildirim' },
        { n: 'Yerler', p: '/admin/places', c: 'Konum' },
        { n: 'Yolculuklar', p: '/admin/trips', c: 'Konum' },
        { n: 'Canlı Harita', p: '/admin/live-map', c: 'Konum' },
        { n: 'Rotalar', p: '/admin/routes', c: 'Konum' },
        { n: 'Konum Geçmişi', p: '/admin/location-history?limit=1', c: 'Konum' },
        { n: 'Acil Durumlar', p: '/admin/emergencies', c: 'Güvenlik' },
        { n: 'SOS Kişileri', p: '/admin/sos-contacts', c: 'Güvenlik' },
        { n: 'Çökme Raporları', p: '/admin/crash-reports', c: 'Güvenlik' },
        { n: 'Varış Kontrolleri', p: '/admin/arrival-checks', c: 'Güvenlik' },
        { n: 'Hikâyeler', p: '/admin/stories', c: 'İçerik' },
        { n: 'Müzik', p: '/admin/music', c: 'İçerik' },
        { n: 'Mesajlar', p: '/admin/messages', c: 'İletişim' },
        { n: 'Hava Durumu', p: '/admin/weather-alerts', c: 'Bilgi' },
        { n: 'İlgi Noktaları', p: '/admin/poi-notifications', c: 'Bilgi' },
        { n: 'Haftalık Özet', p: '/admin/weekly-summaries', c: 'Bilgi' },
        { n: 'Kullanıcı Profili', p: '/users/me', c: 'Kullanıcı API' },
    ];
    const cats = ['all', ...Array.from(new Set(epList.map(e => e.c)))];
    const token = localStorage.getItem('lifeshare_admin_token');
    const hdr = { Authorization: `Bearer ${token}` };

    const fetchStatus = useCallback(async () => {
        try {
            const r = await axios.get(`${API_BASE}/admin/api-status`, { headers: hdr, timeout: 15000 });
            setS(r.data);
        } catch (e) { console.error('API status hatası:', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchHistory = useCallback(async () => {
        try {
            const r = await axios.get(`${API_BASE}/admin/api-status/history?days=90`, { headers: hdr, timeout: 15000 });
            if (r.data) setHistory(r.data);
        } catch (e) { /* history henüz yoksa sessizce devam et */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const chkEp = async (n: string, p: string, c: string): Promise<EndpointStatus> => {
        const t0 = Date.now();
        try {
            await axios.get(`${API_BASE}${p}`, { headers: hdr, timeout: 15000 });
            const ms = Date.now() - t0;
            return { name: n, endpoint: p, status: ms > 5000 ? 'slow' : 'ok', responseTime: ms, category: c };
        } catch (err: any) {
            const ms = Date.now() - t0;
            if (!err.response) return { name: n, endpoint: p, status: 'cors', responseTime: ms, error: 'CORS', category: c };
            if ([401, 403].includes(err.response?.status)) return { name: n, endpoint: p, status: 'ok', responseTime: ms, category: c, error: 'Auth' };
            if (err.response?.status === 405) return { name: n, endpoint: p, status: 'ok', responseTime: ms, category: c, error: 'POST' };
            if (err.response?.status === 404) return { name: n, endpoint: p, status: 'error', responseTime: ms, error: '404', category: c };
            return { name: n, endpoint: p, status: 'error', responseTime: ms, error: `${err.response?.status}`, category: c };
        }
    };

    // The probe list is 26 endpoints, several of which return whole tables, and
    // this page re-runs it every 60s while auto-refresh is on. Firing them all
    // at once was enough to trip the API's own rate limiter (500/min per IP) and
    // to make the panel the heaviest client on the server. Four at a time keeps
    // the whole sweep well under a second in the normal case.
    const PROBE_CONCURRENCY = 4;

    const runBounded = async <T,>(tasks: Array<() => Promise<T>>, limit: number): Promise<T[]> => {
        const results = new Array<T>(tasks.length);
        let next = 0;
        const worker = async () => {
            while (true) {
                const i = next++;
                if (i >= tasks.length) return;
                results[i] = await tasks[i]();
            }
        };
        await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
        return results;
    };

    const chkAll = useCallback(async () => {
        setChecking(true);
        setEps(epList.map(e => ({ name: e.n, endpoint: e.p, status: 'checking' as const, category: e.c })));
        const r = await runBounded(epList.map(e => () => chkEp(e.n, e.p, e.c)), PROBE_CONCURRENCY);
        setEps(r); setLastCheck(new Date());
        setEpSum({ ok: r.filter(x => x.status === 'ok').length, error: r.filter(x => x.status === 'error').length, slow: r.filter(x => x.status === 'slow').length, cors: r.filter(x => x.status === 'cors').length, total: r.length });
        setChecking(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshAll = useCallback(async () => {
        setLoading(true);
        await Promise.all([fetchStatus(), fetchHistory(), chkAll()]);
        setLoading(false);
    }, [fetchStatus, fetchHistory, chkAll]);

    useEffect(() => { refreshAll(); }, [refreshAll]);
    useEffect(() => { if (!autoRef) return; const iv = setInterval(refreshAll, 60000); return () => clearInterval(iv); }, [autoRef, refreshAll]);

    const SvcIcon = ({ icon }: { icon: string }) => {
        const c = "w-5 h-5";
        switch (icon) { case 'database': return <Database className={c} />; case 'zap': return <Zap className={c} />; case 'bell': return <Bell className={c} />; case 'smartphone': return <Smartphone className={c} />; default: return <Server className={c} />; }
    };

    const Bar = ({ v, color }: { v: number; color: string }) => (
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${Math.min(v, 100)}%` }} />
        </div>
    );

    const UBar = ({ daily, label }: { daily: DailyStatus[]; label: string }) => {
        const pad: (DailyStatus | null)[] = [];
        const td = new Date();
        for (let i = 89; i >= 0; i--) { const d = new Date(td); d.setDate(d.getDate() - i); pad.push(daily.find(x => x.date === d.toISOString().slice(0, 10)) || null); }
        return (
            <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-800">{label}</span>
                    <span className="text-xs text-gray-500">{daily.length > 0 ? `%${Math.round(daily.reduce((a, d) => a + d.uptime_pct, 0) / daily.length)} çalışma süresi` : 'Veri toplanıyor...'}</span>
                </div>
                <div className="flex gap-[2px] h-7">
                    {pad.map((day, i) => (
                        <div key={i} className="flex-1 cursor-pointer" onMouseEnter={() => day && setHovDay(day)} onMouseLeave={() => setHovDay(null)}>
                            <div className={`w-full h-7 rounded-sm transition-all ${!day ? 'bg-gray-200' : day.status === 'ok' ? 'bg-green-500 hover:bg-green-400' : day.status === 'warning' ? 'bg-yellow-400 hover:bg-yellow-300' : 'bg-red-500 hover:bg-red-400'}`} />
                        </div>
                    ))}
                </div>
                <div className="flex justify-between text-[10px] text-gray-400"><span>90 gün önce</span><span>Bugün</span></div>
            </div>
        );
    };

    const fEps = selCat === 'all' ? eps : eps.filter(e => e.category === selCat);


    return (
        <div className="space-y-6">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">API Durumu</h1>
                        <p className="text-sm text-gray-500 mt-1">Sunucu sağlığı ve servis izleme</p>
                    </div>
                    <button onClick={refreshAll} disabled={loading || checking} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${(loading || checking) ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Banner */}
                {!s && loading && (
                    <div className="bg-white rounded-2xl p-8 flex items-center justify-center gap-3">
                        <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
                        <span className="text-gray-500 text-sm">Sunucu durumu yükleniyor...</span>
                    </div>
                )}
                {s && (
                    <div className={`rounded-2xl p-5 text-white ${s.overall === 'healthy' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : s.overall === 'warning' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' : 'bg-gradient-to-r from-red-500 to-rose-600'}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                                    {s.overall === 'healthy' ? <CheckCircle className="w-7 h-7" /> : s.overall === 'warning' ? <AlertTriangle className="w-7 h-7" /> : <XCircle className="w-7 h-7" />}
                                </div>
                                <div>
                                    <p className="text-lg font-bold">{s.overall === 'healthy' ? 'Tüm Sistemler Çalışıyor' : s.overall === 'warning' ? 'Bazı Servislerde Uyarı Var' : 'Kritik Sorun Tespit Edildi!'}</p>
                                    <p className="text-sm opacity-80">Çalışma süresi: {s.uptime.formatted} — Son kontrol: {lastCheck?.toLocaleTimeString('tr-TR') || '...'}</p>
                                </div>
                            </div>
                            <div className="text-right hidden md:block">
                                <p className="text-4xl font-bold">{epSum.total > 0 ? Math.round(((epSum.ok + epSum.cors) / epSum.total) * 100) : 0}%</p>
                                <p className="text-sm opacity-80">{epSum.ok}/{epSum.total} endpoint aktif</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Uptime Bars — her zaman göster */}
                <div className="bg-white rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-5">
                        <TrendingUp className="w-5 h-5 text-purple-600" />
                        <span className="font-semibold text-gray-900">Çalışma Süresi Geçmişi (90 Gün)</span>
                        {history && history.overall_uptime_pct !== undefined && (
                            <span className="ml-auto text-lg font-bold text-green-600">%{history.overall_uptime_pct}</span>
                        )}
                    </div>
                    <UBar daily={history?.daily || []} label="Genel API Durumu" />
                    <div className="mt-6 pt-5 border-t border-gray-100 space-y-5">
                        {['db', 'redis', 'apns', 'fcm', 'scheduler'].map(sv => {
                            const svcDaily = (history?.daily || []).map(d => ({
                                ...d,
                                status: d.services[sv] !== undefined ? (d.services[sv] >= 100 ? 'ok' : d.services[sv] >= 90 ? 'warning' : 'error') : 'ok',
                                uptime_pct: d.services[sv] ?? 100
                            }));
                            return <UBar key={sv} daily={svcDaily} label={SVC_LABELS[sv] || sv} />;
                        })}
                    </div>
                </div>

                {/* Tooltip */}
                {hovDay && (
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white border border-gray-200 rounded-xl px-4 py-3  z-50 text-sm text-gray-700">
                        <div className="flex items-center gap-3">
                            <span className={`w-3 h-3 rounded-full ${hovDay.status === 'ok' ? 'bg-green-500' : hovDay.status === 'warning' ? 'bg-yellow-400' : 'bg-red-500'}`} />
                            <span className="font-semibold">{new Date(hovDay.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            <span className="text-gray-300">|</span>
                            <span className="text-green-600 font-mono font-bold">%{hovDay.uptime_pct}</span>
                            <span className="text-gray-300">|</span>
                            <span className="text-gray-500">{hovDay.checks} kontrol</span>
                            {hovDay.error > 0 && <span className="text-red-600 font-medium">{hovDay.error} hata</span>}
                        </div>
                    </div>
                )}

                {/* Services - tıklanabilir detay */}
                {!s && !loading && (
                    <div className="bg-white rounded-2xl p-6 text-center">
                        <AlertTriangle className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-700">Sunucuya Bağlanılamadı</p>
                        <p className="text-xs text-gray-500 mt-1">API sunucusu yanıt vermiyor. Lütfen yenile butonuna basın veya backend loglarını kontrol edin.</p>
                    </div>
                )}
                {s && (
                    <div className="bg-white rounded-2xl overflow-hidden">
                        <div className="p-5 border-b border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Activity className="w-5 h-5 text-purple-600" /> Anlık Servis Durumu</h2>
                        </div>
                        <div className="divide-y divide-gray-100">
                            {s.services.map((svc, i) => {
                                const key = getSvcKey(svc.name);
                                const open = expSvc === svc.name;
                                const desc = SVC_DESC[key]?.[svc.status] || '';
                                return (
                                    <div key={i}>
                                        <div className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer" onClick={() => setExpSvc(open ? null : svc.name)}>
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${svc.status === 'ok' ? 'bg-green-100 text-green-600' : svc.status === 'warning' ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'}`}>
                                                    <SvcIcon icon={svc.icon} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">{svc.name}</p>
                                                    <p className="text-xs text-gray-500">{svc.latency_ms ? `Yanıt: ${svc.latency_ms}ms` : svc.detail || ''}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xs font-medium px-3 py-1 rounded-full ${svc.status === 'ok' ? 'bg-green-100 text-green-700' : svc.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                                    {svc.status === 'ok' ? 'Çalışıyor' : svc.status === 'warning' ? 'Uyarı' : 'Hata'}
                                                </span>
                                                {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                            </div>
                                        </div>
                                        {open && desc && (
                                            <div className={`mx-4 mb-4 p-3 rounded-xl text-sm flex items-start gap-2 ${svc.status === 'ok' ? 'bg-green-50 text-green-800 border border-green-200' : svc.status === 'warning' ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                                                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" /><p>{desc}</p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* System Metrics */}
                {s?.system && (
                    <div className="bg-white rounded-2xl p-5">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4"><Server className="w-5 h-5 text-purple-600" /> Sunucu Kaynakları</h2>
                        <div className="grid grid-cols-3 gap-5">
                            {[
                                { l: 'İşlemci', v: s.system.cpu_percent, sub: `${s.system.cpu_count} çekirdek`, icon: <Cpu className="w-3.5 h-3.5" /> },
                                { l: 'Bellek', v: s.system.memory_percent, sub: `${s.system.memory_used_gb}/${s.system.memory_total_gb} GB`, icon: <Server className="w-3.5 h-3.5" /> },
                                { l: 'Disk', v: s.system.disk_percent, sub: `${s.system.disk_used_gb}/${s.system.disk_total_gb} GB`, icon: <HardDrive className="w-3.5 h-3.5" /> }
                            ].map((m, i) => (
                                <div key={i}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs text-gray-500 flex items-center gap-1">{m.icon} {m.l}</span>
                                        <span className="text-sm font-bold text-gray-900">%{m.v}</span>
                                    </div>
                                    <Bar v={m.v} color={m.v > 80 ? 'bg-red-500' : m.v > 50 ? 'bg-yellow-500' : 'bg-purple-500'} />
                                    <p className="text-[10px] text-gray-400 mt-1">{m.sub}</p>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                            {[{ l: 'Veritabanı', v: `${s.database?.size_mb || 0} MB` }, { l: 'Kullanıcı', v: s.database?.total_users || 0 }, { l: 'Aktif', v: s.database?.active_users || 0 }, { l: 'Cihaz', v: s.database?.active_devices || 0 }, { l: 'iOS', v: s.database?.ios_devices || 0 }, { l: 'Android', v: s.database?.android_devices || 0 }, { l: 'Konum/5dk', v: s.database?.locations_last_5min || 0 }].map((st, i) => (
                                <div key={i} className="text-center"><p className="text-[10px] text-gray-400">{st.l}</p><p className="text-sm font-bold text-gray-900">{st.v}</p></div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Endpoints - tıklanabilir hata detayı */}
                <div className="bg-white rounded-2xl overflow-hidden">
                    <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Wifi className="w-5 h-5 text-purple-600" /> Endpoint Kontrolü ({epSum.total})</h2>
                        <div className="flex items-center gap-3 text-xs">
                            <span className="flex items-center gap-1 text-green-600"><CheckCircle className="w-3 h-3" />{epSum.ok}</span>
                            <span className="flex items-center gap-1 text-orange-500"><AlertTriangle className="w-3 h-3" />{epSum.cors}</span>
                            <span className="flex items-center gap-1 text-red-600"><XCircle className="w-3 h-3" />{epSum.error}</span>
                        </div>
                    </div>
                    <div className="px-5 py-3 border-b border-gray-50 flex flex-wrap gap-1.5">
                        {cats.map(c => (
                            <button key={c} onClick={() => setSelCat(c)} className={`px-3 py-1.5 rounded-xl text-xs font-medium ${selCat === c ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{c === 'all' ? 'Tümü' : c}</button>
                        ))}
                    </div>
                    <div className="divide-y divide-gray-50 max-h-[500px] overflow-y-auto">
                        {fEps.map((ep, idx) => (
                            <div key={idx}>
                                <div className={`flex items-center justify-between px-5 py-3 hover:bg-gray-50 cursor-pointer border-l-4 ${ep.status === 'ok' ? 'border-l-green-500' : ep.status === 'cors' ? 'border-l-orange-400' : ep.status === 'error' ? 'border-l-red-500' : ep.status === 'slow' ? 'border-l-yellow-500' : 'border-l-gray-200'}`}
                                    onClick={() => setExpEp(expEp === idx ? null : idx)}>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-900 truncate">{ep.name}</p>
                                        <p className="text-[10px] text-gray-400 font-mono truncate">{ep.endpoint}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{ep.category}</span>
                                        {ep.responseTime !== undefined && <span className={`text-xs font-mono ${ep.responseTime > 5000 ? 'text-yellow-600' : 'text-gray-400'}`}>{ep.responseTime}ms</span>}
                                        {ep.error && <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${ep.error === '404' ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'}`}>{ep.error}</span>}
                                        {ep.status === 'ok' ? <CheckCircle className="w-4 h-4 text-green-500" /> : ep.status === 'error' ? <XCircle className="w-4 h-4 text-red-500" /> : ep.status === 'checking' ? <div className="w-4 h-4 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin" /> : null}
                                    </div>
                                </div>
                                {expEp === idx && ep.error && EP_ERR[ep.error] && (
                                    <div className={`mx-5 mb-3 p-3 rounded-xl text-sm ${ep.status === 'error' ? 'bg-red-50 border border-red-200' : 'bg-orange-50 border border-orange-200'}`}>
                                        <p className={`font-semibold mb-1 ${ep.status === 'error' ? 'text-red-800' : 'text-orange-800'}`}>{EP_ERR[ep.error].title}</p>
                                        <p className={`text-xs ${ep.status === 'error' ? 'text-red-700' : 'text-orange-700'}`}>{EP_ERR[ep.error].desc}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Scheduler */}
                {s?.scheduler && (
                    <div className="bg-white rounded-2xl overflow-hidden">
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Timer className="w-5 h-5 text-purple-600" /> Zamanlanmış Görevler ({s.scheduler.total})</h2>
                            <span className={`text-xs font-medium px-3 py-1 rounded-full ${s.scheduler.running ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{s.scheduler.running ? 'Çalışıyor' : 'Durmuş!'}</span>
                        </div>
                        <div className="divide-y divide-gray-50">
                            {s.scheduler.jobs.map((j, i) => (
                                <div key={i} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                                    <div><p className="text-sm font-medium text-gray-900">{j.name}</p><p className="text-[10px] text-gray-400 font-mono">{j.id}</p></div>
                                    <div className="flex items-center gap-3">
                                        <p className="text-xs text-gray-500">{j.next_run ? new Date(j.next_run).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</p>
                                        <span className={`w-2.5 h-2.5 rounded-full ${j.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Geçmiş Olaylar - tıklanabilir */}
                <div className="bg-white rounded-2xl overflow-hidden">
                    <div className="p-5 border-b border-gray-100">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Shield className="w-5 h-5 text-purple-600" /> Geçmiş Olaylar</h2>
                    </div>
                    {history?.incidents?.length ? (
                        <div className="divide-y divide-gray-50">
                            {history.incidents.slice(0, 10).map((inc, i) => (
                                <div key={i}>
                                    <div className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => setExpInc(expInc === i ? null : i)}>
                                        <div className="flex items-center gap-3">
                                            <span className={`w-3 h-3 rounded-full ${inc.error > 0 ? 'bg-red-500' : 'bg-yellow-400'}`} />
                                            <span className="text-sm font-medium text-gray-900">{new Date(inc.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${inc.error > 0 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                {inc.error > 0 ? `${inc.error} hata` : `${inc.warning} uyarı`}
                                            </span>
                                            {expInc === i ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                        </div>
                                    </div>
                                    {expInc === i && (
                                        <div className="mx-5 mb-3 p-3 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-700">
                                            <p className="mb-1">Toplam {inc.checks} kontrol yapıldı. Çalışma oranı: <strong>%{inc.uptime_pct}</strong></p>
                                            {Object.entries(inc.services).filter(([, v]) => v < 100).map(([k, v]) => (
                                                <p key={k} className="text-red-600 text-xs">{SVC_LABELS[k] || k}: %{v} — {SVC_DESC[k]?.error || 'Servis sorun yaşadı.'}</p>
                                            ))}
                                            {Object.entries(inc.services).filter(([, v]) => v < 100).length === 0 && <p className="text-xs text-gray-500">Detaylı servis bilgisi mevcut değil.</p>}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-8 text-center">
                            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-700">Sorun Yok</p>
                            <p className="text-xs text-gray-500 mt-1">Son 90 günde herhangi bir kesinti veya hata tespit edilmedi.</p>
                        </div>
                    )}
                </div>

                <div className="text-center text-[10px] text-gray-400 pb-4">
                    LifeShare API Durum Monitörü — Otomatik kontroller her 5 dakikada bir yapılır
                </div>
            </div>
        </div>
    );
};

export default ApiStatus;